<template>
  <el-dialog v-model="newCustomerDialogVisiable"
             :title="t('views.customers.newCustomer')">
    <NewCustomerDialog @success="onCustomerCreated"></NewCustomerDialog>
  </el-dialog>
  <el-form
      @submit.prevent="validateSubmit('appointmentsForm')"
      novalidate
      :model="appointment"
      :rules="rules"
      label-position="top"
      ref="appointmentsForm"
      :class="className"
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('appointmentsForm')"
          />
        </div>

<!--        <div class="md:w-4/12 m-2" v-if="enableClearForm">-->
<!--          <horizontal-button-->
<!--              :title="$t('actions.clearButton')"-->
<!--              isDanger-->
<!--              faIcon="trash"-->
<!--              @click.prevent="clearForm('appointmentsForm')"-->
<!--          />-->
<!--        </div>-->
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('customerAppointment') }}</h1>

      <el-form-item :label="t('customer')" prop="customer"
                    class="input-label" required>
        <div class="flex items-center gap-2 w-full">
          <el-select
              filterable
              clearable
              class="w-full"
              value-key="contactDisplayName"
              v-model="appointment.customer"
              @change="(e) => {appointment.customerID = e ? e.id : undefined;} "
              remote
              :remote-method="searchCustomers"
              placeholder=""
          >
            <el-option
                v-for="option in customers"
                :key="option.id"
                :label="option.name"
                :value="option"
            >
              <span style="float: left">{{ option.name }}</span>
              <span
                v-if="option.phone"
                style="
                  float: right;
                  color: var(--el-text-color-secondary);
                  font-size: 13px;
                "
              >
                {{ option.phone }}
              </span>
            </el-option>
          </el-select>
          <button
              type="button"
              @click="() => {newCustomerDialogVisiable = true}"
              class="w-[200px] px-4 bg-gray-200 text-black rounded-md hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 flex items-center gap-2"
          >
            <Icon icon="line-md:plus"/>
            {{ t('newCustomer') }}
          </button>
        </div>
      </el-form-item>

      <el-form-item v-if="isVetModule || appointment.petID" :label="t('pet')" prop="pet"
                    class="input-label">
        <el-select
            filterable
            clearable
            class="w-full"
            value-key="id"
            v-model="appointment.pet"
            @change="(e) => {appointment.petID = e ? e.id : undefined;} "
            remote
            :remote-method="searchPets"
            placeholder=""
        >
          <el-option
              v-for="option in pets"
              :key="option.id"
              :label="option.name"
              :value="option"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item v-if="isVetModule || appointment.serviceItemID" :label="t('vetService')" prop="vetServiceItem"
                    class="input-label">
        <el-select
            filterable
            clearable
            class="w-full"
            value-key="id"
            v-model="appointment.serviceItem"
            @change="(e) => {appointment.serviceItemID = e ? e.id : undefined;} "
            remote
            :remote-method="searchVetServiceItems"
            placeholder=""
        >
          <el-option
              v-for="option in vetServiceItems"
              :key="option.id"
              :label="option.name"
              :value="option"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item :label="t('date')" prop="requestOnlyAppointmentDate"
                    required>
        <div class="flex gap-2 w-full">
          <div class="flex-1 min-w-0">
            <el-date-picker
                v-model="appointmentDay"
                type="date"
                style="width: 100%"
                :shortcuts="shortcuts"
                :clearable="false"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                :popper-class="`${$ctx.locale == 'ar' ? 'rtl': ''}`"
            />
          </div>
          <el-select
              v-model="appointmentTime"
              filterable
              allow-create
              default-first-option
              dir="ltr"
              style="width: 150px"
              :placeholder="t('time')"
              :aria-label="t('time')"
          >
            <el-option
                v-for="slot in timeSlots"
                :key="slot.value"
                :label="slot.label"
                :value="slot.value"
            />
          </el-select>
        </div>
        <div class="date-shortcuts" role="group" :aria-label="t('quickDate')">
          <span class="date-shortcuts-label">
            <Icon icon="mdi:calendar-fast" width="16" height="16"/>
            {{ t('quickDate') }}
          </span>
          <button
              v-for="shortcut in dateShortcuts"
              :key="shortcut.key"
              type="button"
              class="date-chip"
              :class="{active: appointmentDay === shortcut.day}"
              :aria-pressed="appointmentDay === shortcut.day"
              :title="shortcut.day"
              @click="appointmentDay = shortcut.day"
          >
            <span class="date-chip-label">{{ t(shortcut.key) }}</span>
            <bdi class="date-chip-date">{{ shortcut.preview }}</bdi>
          </button>
        </div>
      </el-form-item>

      <el-form-item :label="isVetModule ? t('vet') : t('optometrist')" prop="optometrist"
                    class="input-label">
        <el-select
            filterable
            clearable
            v-model="appointment.optometristID"
            class="examination-input"
            style="width: 100%"
            placeholder=" "
        >
          <el-option
              v-for="option in optometrists"
              :key="option"
              :label="`${option.name ? option.name : ''}`"
              :value="option.id"
          ></el-option>
        </el-select>
      </el-form-item>

      <el-form-item :label="t('notes')" prop="notes" class="input-label">
        <el-input
            type="textarea"
            v-model="appointment.notes"
            show-word-limit
            clearable
            required
        >
        </el-input>
      </el-form-item>


    </input-section>
  </el-form>
</template>
<script lang="ts">
/* eslint-disable */

import {defineComponent} from 'vue'
import {ElForm, ElMessage} from 'element-plus'

// components
import HorizontalButton from '@/components/HorizontalButton.vue'

// models
import Appointment, {
  appointmentI18nMessages
} from '@/models/customer/Appointment'
import Customer, {customerI18nMessages} from '@/models/customer/Customer'
import Pet, {petI18nMessages} from '@/models/Pet'
import Item from '@/models/inventory/Item'
import {useI18n} from 'vue-i18n'
import NewCustomerDialog from '@/dialogs/customer/NewCustomerDialog.vue'
import User from '@/models/user/User'
import {Plus} from '@element-plus/icons-vue'
import {useSettingsStore} from "@/stores/settingsStore";
import InputSection from "@/components/form/InputSection.vue";
import AlertBadge from "@/components/AlertBadge.vue";
import {Icon} from "@iconify/vue";
import {formatDate} from "@/utils/date";

// Accepts typed times like "10:20", "1155", "2:30 pm"; hours 1-6 without AM/PM mean afternoon
function normalizeTypedTime(value: string): string | null {
  if (!value) return ''
  const match = /^(\d{1,2})(?:[:.\s]?(\d{2}))?\s*(am|pm|ص|م)?$/i.exec(String(value).trim().toLowerCase())
  if (!match) return null
  let hours = Number(match[1])
  const minutes = Number(match[2] ?? 0)
  const meridiem = match[3]
  if (meridiem) {
    if (hours < 1 || hours > 12) return null
    hours = hours % 12 + (meridiem === 'pm' || meridiem === 'م' ? 12 : 0)
  } else if (hours >= 1 && hours <= 6) {
    hours += 12
  }
  if (hours > 23 || minutes > 59) return null
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// 31 Jan + 1 month gives 28/29 Feb, not 2/3 Mar
function addMonthsClamped(from: Date, months: number): Date {
  const lastDay = new Date(from.getFullYear(), from.getMonth() + months + 1, 0).getDate()
  return new Date(from.getFullYear(), from.getMonth() + months, Math.min(from.getDate(), lastDay))
}

export default defineComponent({
  name: 'appointment-form',

  setup() {
    const {t, locale} = useI18n({
      useScope: 'global',
      messages: {
        en: {
          ...customerI18nMessages.en,
          ...appointmentI18nMessages.en,
          ...petI18nMessages.en,
          age: 'Age',
          customerInfo: 'Customer Info',
          notes: 'Notes',
          pleaseSelectACustomer: 'Please select a Customer',
          pleaseSelectAnOptometrist: 'Please select an Optometrist',
          confirm: 'Confirm',
          printID: 'ID Print',
          doctor: 'Doctor',
          month: 'Month',
          newCustomer: 'New Customer',
          vet: 'Vet',
          oneMonth: '1 Month',
          twoMonth: '2 Month',
          threeMonth: '3 Month',
          fourMonth: '4 Month',
          fiveMonth: '5 Month',
          sixMonth: '6 Month',
          sevenMonth: '7 Month',
          eightMonth: '8 Month',
          nineMonth: '9 Month',
          tenMonth: '10 Month',
          elevenMonth: '11 Month',
          oneYear: '12 Month',
          vetService: 'Service',
          time: 'Time',
          quickDate: 'In',
          inOneWeek: '1 week',
          inOneMonth: '1 month',
          inTwoMonths: '2 months',
          inThreeMonths: '3 months',
          inSixMonths: '6 months',
          inOneYear: '1 year'
        },
        ar: {
          ...customerI18nMessages.ar,
          ...appointmentI18nMessages.ar,
          ...petI18nMessages.ar,
          age: 'العمر',
          customerInfo: 'بيانات الزبون',
          notes: 'الملاحظات',
          pleaseSelectACustomer: 'يرجى اختيار زبون',
          pleaseSelectAnOptometrist: 'يرجى اختيار الفاحص',
          confirm: 'اختيار',
          printID: 'طباعة الكرت',
          doctor: 'الطبيب',
          month: 'شهر',
          newCustomer: 'زبون جديد',
          vet: 'الطبيب البيطري',
          oneMonth: '1 شهر',
          twoMonth: '2 شهر',
          threeMonth: '3 شهر',
          fourMonth: '4 شهر',
          fiveMonth: '5 شهر',
          sixMonth: '6 شهر',
          sevenMonth: '7 شهر',
          eightMonth: '8 شهر',
          nineMonth: '9 شهر',
          tenMonth: '10 شهر',
          elevenMonth: '11 شهر',
          oneYear: '12 شهر',
          vetService: 'الخدمة',
          time: 'الوقت',
          quickDate: 'بعد',
          inOneWeek: 'أسبوع',
          inOneMonth: 'شهر',
          inTwoMonths: 'شهرين',
          inThreeMonths: '3 أشهر',
          inSixMonths: '6 أشهر',
          inOneYear: 'سنة'
        }
      }
    })

    const rules = {
      id: [{required: true, message: t('validation.required')}],
      customerID: [
        {
          required: true,
          message: t('validation.required'),
          trigger: 'blur'
        },
      ],
      requestOnlyAppointmentDate: [
        {
          required: true,
          message: t('validation.required'),
          trigger: 'blur'
        },
      ],
    }

    return {t, locale, rules, Plus, useSettingsStore}
  },

  components: {
    Icon,
    AlertBadge, InputSection,
    HorizontalButton,
    NewCustomerDialog
  },
  props: {
    initCustomer: {
      type: Customer,
      required: true
    },
    initPet: {
      type: Pet,
      required: false,
      default: null
    },
    showPetField: {
      type: Boolean,
      required: false,
      default: false
    },
    initAppointment: {
      type: Appointment,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    },
    enablePrint: {
      type: Boolean,
      required: false
    },
    className: {
      type: String,
      default: ''
    },
    initVetId: {
      type: String,
      required: false,
      default: ''
    }
  },

  data() {
    return {
      appointment: this.initAppointment,
      optometrists: [] as User[],
      customer: this.initCustomer,
      customers: [] as Customer[] | any,
      pet: this.initPet,
      pets: [] as Pet[],
      vetServiceItems: [] as Item[],
      error: {title: '', body: ''},
      selectCustomerDialogVisiable: false,
      newCustomerDialogVisiable: false,
      selectedCustomerElSelect: null,
      appointmentDay: '',
      appointmentTime: '',
      shortcuts: [
        {
          text: this.t('oneMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 1, now.getDate())
          },
        },
        {
          text: this.t('twoMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 2, now.getDate())
          },
        },
        {
          text: this.t('threeMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 3, now.getDate())
          },
        },
        {
          text: this.t('fourMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 4, now.getDate())
          },
        },
        {
          text: this.t('fiveMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 5, now.getDate())
          },
        },
        {
          text: this.t('sixMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 6, now.getDate())
          },
        },
        {
          text: this.t('sevenMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 7, now.getDate())
          },
        },
        {
          text: this.t('eightMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 8, now.getDate())
          },
        },
        {
          text: this.t('nineMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 9, now.getDate())
          },
        },
        {
          text: this.t('tenMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 10, now.getDate())
          },
        },
        {
          text: this.t('elevenMonth'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 11, now.getDate())
          },
        },
        {
          text: this.t('oneYear'),
          value: () => {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 12, now.getDate())
          },
        },
      ]
    }
  },
  beforeMount() {

  },
  async mounted() {
    // Initialize customer
    if (this.customer && this.customer.id) {
      this.appointment.customer = this.customer;
      this.appointment.customerID = this.customer.id;
      this.customers = [this.appointment.customer]
    } else if (this.appointment.customer && this.appointment.customer.id) {
      this.customers = [this.appointment.customer]
    }

    // Initialize pet - check both initPet prop and appointment.pet
    if (this.pet && this.pet.id) {
      // Pet passed via prop (e.g., from vet service)
      this.appointment.pet = Pet.from(this.pet);
      this.appointment.petID = this.pet.id;
      this.pets = [this.appointment.pet]
    } else if (this.appointment.pet && this.appointment.pet.id) {
      // Pet already exists in appointment (viewing existing appointment)
      this.appointment.pet = Pet.from(this.appointment.pet)
      this.pets = [this.appointment.pet]
    }

    // Initialize vet service item if it exists in appointment
    if (this.appointment.serviceItem && this.appointment.serviceItem.id) {
      this.appointment.serviceItem = Item.from(this.appointment.serviceItem)
      this.vetServiceItems = [this.appointment.serviceItem]
    }

    if (this.appointment.appointmentDate) {
      const existing = new Date(this.appointment.appointmentDate)
      this.appointmentDay = formatDate(existing, 'YYYY-MM-DD')
      this.appointmentTime = formatDate(existing, 'HH:mm')
      this.syncAppointmentDate()
    } else {
      this.setDefaultAppointmentDate()
    }

    // Fetch vets if vet_module is enabled, otherwise fetch optometrists
    const employeeTitle = this.isVetModule ? 'vets' : 'optometrists'
    const usersRes = await this.$http.get<User[]>(User.ENDPOINT + `/${employeeTitle}`)
    this.optometrists = usersRes.map((user) => User.from(user))

    // Pre-select vet if provided and not already set
    if (this.initVetId && !this.appointment.optometristID) {
      this.appointment.optometristID = this.initVetId
    }
  },

  watch: {
    appointmentDay() {
      this.syncAppointmentDate()
    },
    appointmentTime(value: string, previous: string) {
      const normalized = normalizeTypedTime(value)
      if (normalized === null) {
        this.appointmentTime = normalizeTypedTime(previous) ?? '10:00'
        return
      }
      if (normalized !== value) {
        this.appointmentTime = normalized
        return
      }
      this.syncAppointmentDate()
    }
  },

  computed: {
    dateShortcuts(): { key: string, day: string, preview: string }[] {
      const steps: [string, number, number][] = [
        ['inOneWeek', 7, 0],
        ['inOneMonth', 0, 1],
        ['inTwoMonths', 0, 2],
        ['inThreeMonths', 0, 3],
        ['inSixMonths', 0, 6],
        ['inOneYear', 0, 12]
      ]
      const today = new Date()
      return steps.map(([key, days, months]) => {
        const date = addMonthsClamped(today, months)
        date.setDate(date.getDate() + days)
        return {key, day: formatDate(date, 'YYYY-MM-DD'), preview: formatDate(date, date.getFullYear() === today.getFullYear() ? 'DD/MM' : 'DD/MM/YY')}
      })
    },
    timeSlots(): { value: string, label: string }[] {
      const values: string[] = []
      for (let minutes = 7 * 60; minutes < 24 * 60; minutes += 15) {
        values.push(`${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`)
      }
      if (this.appointmentTime && !values.includes(this.appointmentTime)) {
        values.push(this.appointmentTime)
        values.sort()
      }
      return values.map((value) => {
        const [h, m] = value.split(':').map(Number)
        return {value, label: formatDate(new Date(2000, 0, 1, h, m), 'hh:mm A')}
      })
    },
    isVetModule(): boolean {
      return useSettingsStore().getValue('vet_module') === 'true'
    }
  },
  methods: {
    setDefaultAppointmentDate() {
      const today = new Date()
      this.appointmentDay = formatDate(today, 'YYYY-MM-DD')
      this.appointmentTime = '10:00'
      this.syncAppointmentDate()
    },

    syncAppointmentDate() {
      if (!this.appointmentDay || !this.appointmentTime) {
        this.appointment.requestOnlyAppointmentDate = undefined
        return
      }
      this.appointment.requestOnlyAppointmentDate =
          formatDate(new Date(`${this.appointmentDay}T${this.appointmentTime}:00`), 'YYYY-MM-DD hh:mm:ss A')
    },

    async searchCustomers(query: string) {
      if (!query) {
        query = ''
      }
      try {
        const url = `${Customer.ENDPOINT}?q=${query}`
        const customersRes = await this.$http.get<Customer[]>(url)
        this.customers = customersRes.map((customer) => Customer.from(customer))
      } catch (error) {
        this.$alertModal.showDanger({title: error.title})
        this.customers.value = []
      }
    },

    async searchPets(query: string) {
      if (!query) {
        query = ''
      }
      try {
        const url = `${Pet.ENDPOINT}?q=${query}`
        const petsRes = await this.$http.get<Pet[]>(url)
        this.pets = petsRes.map((pet) => Pet.from(pet))
      } catch (error) {
        this.$alertModal.showDanger({title: error.title})
        this.pets = []
      }
    },

    async searchVetServiceItems(query: string) {
      if (!query) {
        query = ''
      }
      try {
        const url = `${Item.ENDPOINT}/vet-service-items-lookup?q=${query}`
        const itemsRes = await this.$http.get<Item[]>(url)
        this.vetServiceItems = itemsRes.map((item) => Item.from(item))
      } catch (error) {
        this.$alertModal.showDanger({title: error.title})
        this.vetServiceItems = []
      }
    },

    confirmSelectCustomer() {
      this.customer = this.selectedCustomerElSelect! as Customer
      this.appointment.customerID = this.customer.id
      this.selectCustomerDialogVisiable = false
    },


    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      this.appointment.notes = undefined
      form.resetFields()
      this.setDefaultAppointmentDate()
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        }

        if (!this.appointment.customerID) {
          ElMessage.error({
            dangerouslyUseHTMLString: true,
            message: `<h2>${this.t('pleaseSelectACustomer')}</h2>`
          })
          return false
        }

        // if (!this.appointment.optometristID) {
        //   ElMessage.error({
        //     dangerouslyUseHTMLString: true,
        //     message: `<h2>${this.t('pleaseSelectAnOptometrist')}</h2>`
        //   })
        //   return false
        // }

        const updatedObj = {...this.appointment} as Appointment

        if (updatedObj.optometrist && updatedObj.optometristID) {
          if (updatedObj.optometrist.id != updatedObj.optometristID) {
            updatedObj.optometrist = undefined
          }
        }

        for (const key in updatedObj) {
          if (updatedObj.hasOwnProperty(key)) {
            const typedKey = key as keyof Appointment
            if (updatedObj[typedKey] === undefined) {
              // @ts-ignore
              updatedObj[typedKey] = null
            }
          }
        }
        const resp = await this.submitAction(updatedObj)

        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        } else {
          if (this.clearFormOnSubmit) {
            this.clearForm(formName)
          }

          this.$alertModal.showSuccess({title: this.successMsg, body: ''})
        }
        return true
      })
    },
    onCustomerCreated(customer: Customer) {
      try {
        this.$nextTick(() => {
          this.customers = [...this.customers, customer]
          this.appointment.customer = customer
          this.appointment.customerID = customer.id
          this.newCustomerDialogVisiable = false
        })
      } catch (e) {
        console.log(e)
      }
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply text-lg py-3 font-bold;
}

form > h2 {
  @apply py-3 font-bold;
}

formt > h3 {
  @apply py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}

.select-customer-btn {
  position: absolute;
  right: 15px;
  height: 24px;
  width: 32px;
  top: 4px;
}

.ar .select-customer-btn {
  left: 15px;
  top: 7px;
  right: unset;
}

.dir-rtl {
  direction: rtl;
}

.date-shortcuts {
  @apply flex flex-wrap items-center w-full mt-2;
  gap: 6px;
}

.date-shortcuts-label {
  @apply inline-flex items-center gap-1 text-xs font-semibold text-gray-500;
  margin-inline-end: 2px;
}

.date-chip {
  @apply inline-flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700;
  min-width: 68px;
  padding: 4px 10px;
  line-height: 1.15;
  transition: border-color .15s, background-color .15s, color .15s;
}

.date-chip:hover,
.date-chip:focus-visible {
  border-color: var(--primary-bg-color, #1D4C75);
  color: var(--primary-bg-color, #1D4C75);
  outline: none;
}

.date-chip.active {
  background: var(--primary-bg-color, #1D4C75);
  border-color: var(--primary-bg-color, #1D4C75);
  color: #fff;
}

.date-chip-label {
  @apply text-sm font-bold;
}

.date-chip-date {
  @apply text-gray-400;
  font-size: 11px;
}

.date-chip.active .date-chip-date {
  color: rgba(255, 255, 255, .75);
}

@media (max-width: 767px) {
  .date-shortcuts {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .date-shortcuts-label {
    grid-column: 1 / -1;
  }

  .date-chip {
    min-width: 0;
  }
}

.custom-required:before {
  content: '*';
  color: var(--el-color-danger);
  margin-right: 4px;
}
</style>
